import React, { useEffect, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCTION_URL_DEFAULT = 'https://project-crm-merge.emergent.host';

const ContentSync = () => {
  const [targetUrl, setTargetUrl] = useState(
    localStorage.getItem('syncTargetUrl') || PRODUCTION_URL_DEFAULT
  );
  const [username, setUsername] = useState(
    localStorage.getItem('syncUsername') || 'admin'
  );
  const [password, setPassword] = useState('');
  const [includeStaff, setIncludeStaff] = useState(true);
  const [includeStories, setIncludeStories] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Read local summary of preview data so the user knows what will be pushed
  const [preview, setPreview] = useState({ staff: 0, stories: 0 });

  const authHeader = `Basic ${localStorage.getItem('crmAuth') || ''}`;

  useEffect(() => {
    (async () => {
      try {
        const [s1, s2] = await Promise.all([
          fetch(`${BACKEND_URL}/api/crm/staff`, { headers: { Authorization: authHeader } }).then((r) => r.json()),
          fetch(`${BACKEND_URL}/api/crm/stories`, { headers: { Authorization: authHeader } }).then((r) => r.json()),
        ]);
        setPreview({
          staff: Array.isArray(s1) ? s1.length : 0,
          stories: Array.isArray(s2) ? s2.filter((s) => s.status === 'published').length : 0,
        });
      } catch (_e) { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  const runSync = async () => {
    if (!targetUrl.startsWith('http')) {
      setError('Target URL must be a full http(s) URL.');
      return;
    }
    if (!username || !password) {
      setError('Enter the production CRM username and password.');
      return;
    }
    if (!window.confirm(
      `This will PUSH your current content to:\n\n${targetUrl}\n\n` +
      `Pushing:\n` +
      (includeStaff ? `  • ${preview.staff} staff profiles\n` : '') +
      (includeStories ? `  • ${preview.stories} published impact stories\n` : '') +
      `\nExisting records on the target will be duplicated (not overwritten). Continue?`
    )) return;

    localStorage.setItem('syncTargetUrl', targetUrl);
    localStorage.setItem('syncUsername', username);
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/crm/sync/content-to-production`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({
          target_url: targetUrl,
          username,
          password,
          include: { staff: includeStaff, stories: includeStories },
          self_url: BACKEND_URL,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || `Sync failed (${res.status})`);
      }
      setResult(data);
    } catch (e) {
      setError(e.message || 'Sync failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">🔄 Sync Preview → Production</h2>
        <p className="text-gray-600 text-sm mt-1">
          Push your current staff profiles and published impact stories from this preview
          environment to the production site ({PRODUCTION_URL_DEFAULT}).
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 text-sm text-yellow-900">
        <strong>Important:</strong> This action <em>appends</em> records on the target.
        Re-running it will create duplicates. If the target already has the content, skip this step.
      </div>

      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Target backend URL</label>
          <input
            type="url"
            data-testid="sync-target-url"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
          />
          <p className="text-xs text-gray-500 mt-1">The backend URL the production frontend talks to.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Production admin username</label>
            <input
              type="text"
              data-testid="sync-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Production admin password</label>
            <input
              type="password"
              data-testid="sync-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kioo-primary"
            />
            <p className="text-xs text-gray-500 mt-1">Never stored — used once for this sync.</p>
          </div>
        </div>

        <div>
          <p className="block text-sm font-medium text-gray-700 mb-2">What to push</p>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="sync-include-staff"
                checked={includeStaff}
                onChange={(e) => setIncludeStaff(e.target.checked)}
              />
              <span>👥 Staff profiles ({preview.staff} here)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="sync-include-stories"
                checked={includeStories}
                onChange={(e) => setIncludeStories(e.target.checked)}
              />
              <span>📖 Published impact stories ({preview.stories} here)</span>
            </label>
          </div>
        </div>

        <button
          type="button"
          onClick={runSync}
          disabled={loading}
          data-testid="sync-run-button"
          className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-bold hover:from-emerald-700 hover:to-emerald-800 disabled:opacity-60 transition-all shadow"
        >
          {loading ? 'Syncing…' : '🚀 Push to production'}
        </button>

        {error && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-200" data-testid="sync-error">
            {error}
          </div>
        )}

        {result && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl space-y-2" data-testid="sync-result">
            <div className="font-semibold text-emerald-900">✅ Sync complete</div>
            <div className="text-sm text-gray-800">
              <div>👥 Staff — pushed: <strong>{result.staff?.pushed ?? 0}</strong>, failed: {result.staff?.failed ?? 0}</div>
              <div>📖 Stories — pushed: <strong>{result.stories?.pushed ?? 0}</strong>, failed: {result.stories?.failed ?? 0}</div>
              {((result.staff?.errors || []).length + (result.stories?.errors || []).length) > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-amber-700">Show errors</summary>
                  <ul className="list-disc pl-5 text-xs text-gray-700 mt-1">
                    {(result.staff?.errors || []).map((e, i) => <li key={`se-${i}`}>Staff: {e}</li>)}
                    {(result.stories?.errors || []).map((e, i) => <li key={`st-${i}`}>Story: {e}</li>)}
                  </ul>
                </details>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentSync;
